import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SearchCoordinates, CompanySearchRequest, WorkflowStepType, Company } from '@warema/shared/models';
import { WorkflowValidationApiService } from '@warema/shared/workflows/workflow-common/api';
import { map, Observable } from 'rxjs';
import * as SearchActions from '../state/search.actions';
import { SearchState } from '../state/search.reducers';
import * as SearchSelectors from '../state/search.selectors';

@Injectable({
    providedIn: 'root',
})
export class SearchDataService {
    private readonly _store = inject<Store<SearchState>>(Store);
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _workflowValidationApiService = inject(WorkflowValidationApiService);

    public companies$: Observable<Array<Company>>;
    public selectedCompany$: Observable<Company>;
    public instantOfferValue$: Observable<boolean>;
    public selectedCompanyAddressId$: Observable<string>;
    public isSearchRunning$: Observable<boolean>;
    public noSearchResults$: Observable<boolean | null>;
    public canSearchMore$: Observable<boolean>;
    public latestSearchedCoordinates$: Observable<SearchCoordinates>;

    constructor() {
        this.companies$ = this._store.select(SearchSelectors.selectAllCompanies);
        this.selectedCompany$ = this._store.select(SearchSelectors.selectSelectedCompany);
        this.instantOfferValue$ = this._store.select(SearchSelectors.selectInstantOfferValue);
        this.selectedCompanyAddressId$ = this._store.select(SearchSelectors.selectSelectedCompanyAddressId);
        this.isSearchRunning$ = this._store.select(SearchSelectors.selectIsSearchRunning);
        this.noSearchResults$ = this._store.select(SearchSelectors.selectNoSearchResults);
        this.canSearchMore$ = this._store.select(SearchSelectors.selectCanSearchMore);
        this.latestSearchedCoordinates$ = this._store.select(SearchSelectors.selectLatestSearchedCoordinates);
    }

    public validateSearch(): void {
        const isValid = this.selectedCompany$.pipe(map(x => !!x));
        this._workflowValidationApiService.manageWorkflowValidation(isValid, WorkflowStepType.Search);
    }

    public initializeSearchCoordinatesForRegionalSea(coordinates: SearchCoordinates): void {
        this._handleSearchCoordinatesChange(coordinates);
    }

    public searchCompanies(search: CompanySearchRequest): void {
        this._store.dispatch(SearchActions.isSearchRunningStatusChanged({ isSearchRunning: true }));
        // first set the store with the latitude and longitude included (info that is not stored in url)
        this._handleSearchCoordinatesChange({
            address: search.searchAddress,
            latitude: search.latitude,
            longitude: search.longitude,
        });
        void this._router.navigate([], {
            relativeTo: this._route,
            queryParams: { location: search.searchAddress },
            queryParamsHandling: 'merge',
        });

        this._store.dispatch(SearchActions.SearchCompanies({ search }));
    }

    public searchMoreCompanies(search: CompanySearchRequest): void {
        this._store.dispatch(SearchActions.isSearchRunningStatusChanged({ isSearchRunning: true }));
        this._store.dispatch(SearchActions.SearchMoreCompanies({ search }));
    }

    public selectCompany(id: string): void {
        this._store.dispatch(SearchActions.CompanySelected({ id }));
    }

    public toggleInstanOffer(instantOffer: boolean): void {
        this._store.dispatch(SearchActions.InstantOfferToggled({ instantOffer }));
    }

    public clearCompanySearchDataExceptLocationAndInstantOffer(): void {
        this._store.dispatch(SearchActions.clearCompanySearchDataExceptLocationAndInstantOffer());
    }

    public clearCompanySearchData(): void {
        this._store.dispatch(SearchActions.clearCompanySearchData());
    }

    public specificCompanyAutoSelected(company: Company): void {
        this._store.dispatch(SearchActions.SpecificCompanyAutoSelected({ company }));
    }

    public async handleInstantOfferToggleChange(isInstantOffer: boolean): Promise<void> {
        await this._router.navigate([], {
            relativeTo: this._route,
            queryParams: { instantOffer: isInstantOffer.toString() },
            queryParamsHandling: 'merge',
        });
    }

    private _handleSearchCoordinatesChange(coordinates: SearchCoordinates): void {
        this._store.dispatch(
            SearchActions.searchCoordinatesChanged({
                searchCoordinates: {
                    address: coordinates.address,
                    latitude: coordinates.latitude,
                    longitude: coordinates.longitude,
                },
            }),
        );
    }
}
